import { useMemo, useState } from 'react';
import { SearchInput } from '../../SearchInput';
import { Select } from '../../Select';
import { GenericTable, GenericTableProps } from '../GenericTable';
import { ObjectWithId } from '../types';
import clsx from 'clsx';

export type SearchTableProps<T extends ObjectWithId> = {
  rows: T[];
  getSummary: (row: T) => string;
  filterColumn: keyof T;
  filterLabel?: string;
  searchPlaceholder?: string;
  isSelectable?: boolean;
  onSelectRow?: (selected: T) => void;
  isFetching?: boolean;
  TranslateEnum?: (key: string) => string;
  selectableAccordionTitle?: string;
  AboveHeader?: GenericTableProps<T>['AboveHeader'];
  onFilterChange?: (s: string) => void;
};

export function SearchTable<T extends ObjectWithId>({
  rows,
  getSummary,
  filterColumn,
  filterLabel = 'Filtrar por',
  searchPlaceholder = 'Pesquise aqui',
  isSelectable,
  isFetching,
  onSelectRow,
  TranslateEnum,
  selectableAccordionTitle,
  AboveHeader,
  onFilterChange,
}: SearchTableProps<T>) {
  const [search, setSearch] = useState<string>('');
  const [filterBy, setFilterBy] = useState<string>('');

  const options = useMemo(() => {
    const categoryValues = rows.map((row) => row[filterColumn].toString());
    const uniqueCategoryValues = new Set(categoryValues);

    const opts = Array.from(uniqueCategoryValues).map((value) => ({
      value: TranslateEnum ? TranslateEnum(value) : value,
      label: TranslateEnum ? TranslateEnum(value) : value,
    }));

    return [{ value: 'Todos', label: 'Todos' }, ...opts];
  }, [rows, filterColumn, TranslateEnum]);

  const filteredRows = useMemo(() => {
    if (!search && !filterBy) return rows;

    const filteredRs = rows.filter((row) => {
      let isFilteredBySearch = true;
      let isFilteredByColumn = true;

      if (search)
        isFilteredBySearch = Object.values(row).some((value) =>
          value.toString().toLowerCase().includes(search.toLowerCase())
        );

      if (filterBy)
        isFilteredByColumn =
          row[filterColumn].toString() === filterBy || filterBy === 'Todos';

      return isFilteredBySearch && isFilteredByColumn;
    });

    return filteredRs;
  }, [search, filterBy, rows, filterColumn]);

  return (
    <GenericTable
      isFetching={isFetching}
      rows={filteredRows}
      getSummary={getSummary}
      isSelectable={isSelectable}
      onSelectRow={onSelectRow}
      selectableAccordionTitle={selectableAccordionTitle}
      Footer={<div></div>}
      AboveHeader={
        <div className="flex flex-col gap-4">
          <div
            className={clsx(
              'gap-4 items-center',
              'sm:grid sm:grid-cols-3',
              'flex flex-col'
            )}
          >
            <SearchInput
              placeholder={searchPlaceholder}
              value={search}
              onChange={setSearch}
              className="col-span-2"
              gray
            />
            <Select
              id="filter-table"
              className={clsx('font-semibold text-left border-0')}
              size="small"
              label={filterLabel}
              options={options}
              value={filterBy}
              onChange={(newFilter) => {
                setFilterBy(newFilter);
                onFilterChange?.(newFilter);
              }}
            />
          </div>

          {AboveHeader}
        </div>
      }
    />
  );
}
